import { useCallback, useState, useMemo, useEffect, useRef } from "react";
import { ReactFlow, ReactFlowProvider, Background, Controls, useReactFlow, ConnectionLineType, type Node, type Edge, applyNodeChanges, applyEdgeChanges, addEdge, type NodeChange, type EdgeChange, type Connection, type OnNodesChange, type OnEdgesChange, type OnConnect, type EdgeMouseHandler } from "@xyflow/react";
import "@xyflow/react/dist/style.css";

// Usiamo la gestione nativa di React Flow per le label, aggiungendo solo il drag handler quando necessario
import { StartNode, AgentNode, DecisionNode, ActionNode, EmailNode, EndNode } from "./nodes";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { TextArea } from "@/components/base/textarea/textarea";
import { Modal, DialogTrigger, Dialog, ModalOverlay } from "@/components/application/modals/modal";
import { X, CheckCircle, Users01, ChevronLeft, ArrowLeft, Edit01, Play, HelpCircle, Settings01, XCircle, Atom02, RefreshCcw01, AlertCircle, Clock, CheckCircleBroken, InfoCircle, Lightbulb02, Mail01 } from "@untitledui/icons";
import { Toggle } from "@/components/base/toggle/toggle";
import { ButtonGroup, ButtonGroupItem } from "@/components/base/button-group/button-group";
import { BadgeWithIcon } from "@/components/base/badges/badges";
import { accountService } from "@/data/mock-accounts";
import type { WorkflowNode, WorkflowEdge, NodeType, WorkflowStatus } from "@/types/workflow";
import type { Account } from "@/types/account";
import { useTranslation } from "react-i18next";
import { useAgents } from "@/hooks/api/use-agents";
import { useTriggerTypes, useWorkflowActions } from "@/hooks/api/use-workflow-config";
import { useOutboundEmailTemplates } from "@/hooks/api/use-outbound-email-templates";
import { AgentSelector } from "./agent-selector";
import { Select } from "@/components/base/select/select";
import type { ActionReference } from "@/types/workflow";
import { Link } from "react-router";

interface AiFeedbackItem {
    node_id?: string;
    type: string;
    message: string;
    suggestions?: string[];
}

interface WorkflowEditorProps {
    initialNodes: WorkflowNode[];
    initialEdges: WorkflowEdge[];
    initialAccountIds?: string[];
    initialWorkflowName?: string;
    initialStatus?: WorkflowStatus;  // AI-managed (read-only display)
    initialIsActive?: boolean;       // User can toggle this
    initialIsDefault?: boolean;
    initialAiFeedback?: AiFeedbackItem[];  // AI evaluation feedback
    initialAnalyzedAt?: string;  // When workflow was last analyzed
    onSave: (nodes: WorkflowNode[], edges: WorkflowEdge[], accountIds?: string[], workflowName?: string, isActive?: boolean, isDefault?: boolean) => void;
    onEvaluate?: () => Promise<void>;  // Trigger AI re-analysis
    onCancel: () => void;
    workflowName?: string;
    onTitleChange?: (name: string) => void;
    showBackButton?: boolean;
    backUrl?: string;
}

const nodeTypes = {
    start: StartNode,
    agent: AgentNode,
    decision: DecisionNode,
    action: ActionNode,
    email: EmailNode,
    end: EndNode,
};

// Helper to get badge color and icon for workflow status
const getStatusBadgeConfig = (status: WorkflowStatus): { color: "gray" | "brand" | "success" | "warning" | "error"; icon: typeof CheckCircle } => {
    switch (status) {
        case 'draft':
            return { color: 'gray', icon: Clock };
        case 'analyzing':
            return { color: 'brand', icon: RefreshCcw01 };
        case 'ready':
            return { color: 'success', icon: CheckCircle };
        case 'needs_clarification':
            return { color: 'warning', icon: AlertCircle };
        case 'invalid':
            return { color: 'error', icon: XCircle };
        case 'disabled':
            return { color: 'gray', icon: CheckCircleBroken };
        default:
            return { color: 'gray', icon: Clock };
    }
};

// Component to provide ReactFlow functions to parent
const FlowHelper = ({ onFlowReady }: { onFlowReady: (helpers: { screenToFlowPosition: (position: { x: number; y: number }) => { x: number; y: number }; getViewport: () => { x: number; y: number; zoom: number } }) => void }) => {
    const { screenToFlowPosition, getViewport } = useReactFlow();

    useEffect(() => {
        onFlowReady({ screenToFlowPosition, getViewport });
    }, [screenToFlowPosition, getViewport, onFlowReady]);

    return null;
};

// Inner component that has access to ReactFlow context
const WorkflowEditorInner = ({ initialNodes, initialEdges, initialAccountIds = [], initialWorkflowName, initialStatus, initialIsActive, initialIsDefault, initialAiFeedback, initialAnalyzedAt, onSave, onEvaluate, workflowName: propWorkflowName, onTitleChange, showBackButton, backUrl }: WorkflowEditorProps) => {
    const { t } = useTranslation(['pages:workflows-admin', 'common']);
    const { data: agents = [], isLoading: isLoadingAgents } = useAgents();
    const { data: triggerTypes = [], isLoading: isLoadingTriggers } = useTriggerTypes();
    const { data: workflowActions = [], isLoading: isLoadingActions } = useWorkflowActions();
    const canvasRef = useRef<HTMLDivElement>(null);
    const flowHelpersRef = useRef<{ screenToFlowPosition: (position: { x: number; y: number }) => { x: number; y: number }; getViewport: () => { x: number; y: number; zoom: number } } | null>(null);

    // Workflow name state
    const [workflowName, setWorkflowName] = useState<string>(initialWorkflowName || '');

    // Workflow status (read-only, AI-managed) and activation state
    const workflowStatus = initialStatus || 'draft';  // Read-only from props
    const [isActive, setIsActive] = useState<boolean>(initialIsActive || false);
    const [isDefault, setIsDefault] = useState<boolean>(initialIsDefault || false);
    const [isEvaluating, setIsEvaluating] = useState<boolean>(false);

    // Title editing state
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const titleInputRef = useRef<HTMLInputElement>(null);

    // Sidebar resize state
    const [sidebarWidth, setSidebarWidth] = useState(320); // Default width in pixels (narrower)
    const [isResizing, setIsResizing] = useState(false);
    const sidebarRef = useRef<HTMLDivElement>(null);

    // Modal states
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [isAccountsModalOpen, setIsAccountsModalOpen] = useState(false);

    // AI Feedback Panel state (separate from node properties sidebar)
    const [isAiFeedbackPanelOpen, setIsAiFeedbackPanelOpen] = useState(false);
    const [aiFeedbackPanelWidth, setAiFeedbackPanelWidth] = useState(350);
    const [isResizingAiFeedback, setIsResizingAiFeedback] = useState(false);
    const aiFeedbackPanelRef = useRef<HTMLDivElement>(null);

    // Account selection state
    const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>(initialAccountIds);
    const [accountSearchTerm, setAccountSearchTerm] = useState('');

    // Load available accounts (only SHIPPER type)
    const shipperAccounts = useMemo<Account[]>(() => {
        return accountService.getAll().filter((acc) => acc.userType === "SHIPPER");
    }, []);

    // Filter accounts by search term
    const filteredAccounts = useMemo<Account[]>(() => {
        if (!accountSearchTerm) return shipperAccounts;
        const searchLower = accountSearchTerm.toLowerCase();
        return shipperAccounts.filter(
            (acc) =>
                acc.legalName.toLowerCase().includes(searchLower) ||
                acc.email.toLowerCase().includes(searchLower) ||
                acc.taxId.toLowerCase().includes(searchLower)
        );
    }, [shipperAccounts, accountSearchTerm]);

    // Handle account selection toggle
    const handleAccountToggle = useCallback((accountId: string) => {
        setSelectedAccountIds((prev) => {
            if (prev.includes(accountId)) {
                return prev.filter((id) => id !== accountId);
            } else {
                return [...prev, accountId];
            }
        });
    }, []);

    // Handle modal close and save
    const handleAccountsModalClose = useCallback(() => {
        setIsAccountsModalOpen(false);
        setAccountSearchTerm('');
    }, []);

    // Convert to ReactFlow format
    const [nodes, setNodes] = useState<Node[]>(
        initialNodes.map((node) => ({
            id: node.id,
            type: node.type,
            position: node.position,
            width: node.width,
            height: node.height,
            data: node.data,
        }))
    );

    const [edges, setEdges] = useState<Edge[]>(
        initialEdges.map((edge) => ({
            id: edge.id,
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle,
            targetHandle: edge.targetHandle,
            // Usa la proprietà label nativa di React Flow per il posizionamento automatico
            label: edge.label,
            type: 'smoothstep',
            animated: true,
        }))
    );

    // Rimosso labelPositions - usiamo solo il posizionamento nativo di React Flow

    const [selectedNode, setSelectedNode] = useState<Node | null>(null);
    const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [copiedNode, setCopiedNode] = useState<Node | null>(null);
    const [buttonGroupSelection, setButtonGroupSelection] = useState<Set<string>>(new Set());

    const emailNodeTemplateFilter = useMemo(() => {
        if (selectedNode?.type !== "email") return { system_only: true as const };
        const data = selectedNode.data || {};
        if (data.template_filter === "company" && (data.template_company_ids as string[] | undefined)?.[0]) {
            return { company_id: (data.template_company_ids as string[])[0] };
        }
        return { system_only: true as const };
    }, [selectedNode?.id, selectedNode?.type, selectedNode?.data?.template_filter, selectedNode?.data?.template_company_ids]);
    const { data: outboundTemplates = [], isLoading: isLoadingTemplates } = useOutboundEmailTemplates(emailNodeTemplateFilter);

    // Sync workflow name when initialWorkflowName or propWorkflowName changes
    useEffect(() => {
        if (propWorkflowName !== undefined) {
            setWorkflowName(propWorkflowName);
        } else if (initialWorkflowName !== undefined) {
            setWorkflowName(initialWorkflowName);
        }
    }, [initialWorkflowName, propWorkflowName]);

    // Sync is_active and is_default when initial values change
    useEffect(() => {
        if (initialIsActive !== undefined) {
            setIsActive(initialIsActive);
        }
    }, [initialIsActive]);

    useEffect(() => {
        if (initialIsDefault !== undefined) {
            setIsDefault(initialIsDefault);
        }
    }, [initialIsDefault]);


    // Handle title editing
    const handleTitleClick = useCallback(() => {
        setIsEditingTitle(true);
        setTimeout(() => {
            titleInputRef.current?.focus();
            titleInputRef.current?.select();
        }, 0);
    }, []);

    const handleTitleBlur = useCallback(() => {
        setIsEditingTitle(false);
        if (titleInputRef.current) {
            const newName = titleInputRef.current.value.trim();
            if (newName && newName !== workflowName) {
                setWorkflowName(newName);
                onTitleChange?.(newName);
            }
        }
    }, [workflowName, onTitleChange]);

    const handleTitleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.currentTarget.blur();
        } else if (e.key === 'Escape') {
            if (titleInputRef.current) {
                titleInputRef.current.value = workflowName || '';
            }
            setIsEditingTitle(false);
        }
    }, [workflowName]);

    // Auto-open/close sidebar based on selection
    useEffect(() => {
        if (selectedNode || selectedEdge) {
            setIsSidebarOpen(true);
        } else {
            setIsSidebarOpen(false);
        }
    }, [selectedNode, selectedEdge]);

    // Handle keyboard shortcuts for node duplication (Ctrl/Cmd+C and Ctrl/Cmd+V)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Ctrl/Cmd+C: Copy node (only for non-start/end nodes)
            if ((e.metaKey || e.ctrlKey) && e.key === 'c' && selectedNode) {
                if (selectedNode.type !== 'start' && selectedNode.type !== 'end') {
                    e.preventDefault();
                    e.stopPropagation();
                    // Deep copy the node with all metadata
                    setCopiedNode({
                        ...selectedNode,
                        data: { ...selectedNode.data },
                        position: { ...selectedNode.position },
                    });
                }
            }
            // Ctrl/Cmd+V: Paste/duplicate node
            if ((e.metaKey || e.ctrlKey) && e.key === 'v' && copiedNode) {
                e.preventDefault();
                e.stopPropagation();
                // Create new node with unique ID and offset position
                const newNodeId = `node-${Date.now()}`;
                const offsetX = 50;
                const offsetY = 50;
                const newNode: Node = {
                    ...copiedNode,
                    id: newNodeId,
                    position: {
                        x: copiedNode.position.x + offsetX,
                        y: copiedNode.position.y + offsetY,
                    },
                    data: { ...copiedNode.data },
                };
                setNodes((nds) => [...nds, newNode]);
                // Select the newly created node
                setSelectedNode(newNode);
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);
        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [selectedNode, copiedNode, setNodes]);

    // Sidebar resize handlers
    const handleResizeStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    useEffect(() => {
        const handleResizeMove = (e: MouseEvent) => {
            if (!isResizing || !sidebarRef.current) return;

            const containerRect = sidebarRef.current.parentElement?.getBoundingClientRect();
            if (!containerRect) return;

            // Calculate new width from right edge
            const newWidth = containerRect.right - e.clientX;
            const minWidth = 280;
            const maxWidth = Math.min(500, window.innerWidth * 0.4);

            if (newWidth >= minWidth && newWidth <= maxWidth) {
                setSidebarWidth(newWidth);
            }
        };

        const handleResizeEnd = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleResizeMove);
            document.addEventListener('mouseup', handleResizeEnd);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        }

        return () => {
            document.removeEventListener('mousemove', handleResizeMove);
            document.removeEventListener('mouseup', handleResizeEnd);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isResizing]);

    // AI Feedback Panel resize handler
    const handleAiFeedbackResizeStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizingAiFeedback(true);
    }, []);

    useEffect(() => {
        const handleAiFeedbackResizeMove = (e: MouseEvent) => {
            if (!isResizingAiFeedback || !aiFeedbackPanelRef.current) return;

            const containerRect = aiFeedbackPanelRef.current.parentElement?.getBoundingClientRect();
            if (!containerRect) return;

            // Calculate new width from right edge
            const newWidth = containerRect.right - e.clientX;
            const minWidth = 300;
            const maxWidth = Math.min(600, window.innerWidth * 0.5);

            if (newWidth >= minWidth && newWidth <= maxWidth) {
                setAiFeedbackPanelWidth(newWidth);
            }
        };

        const handleAiFeedbackResizeEnd = () => {
            setIsResizingAiFeedback(false);
        };

        if (isResizingAiFeedback) {
            document.addEventListener('mousemove', handleAiFeedbackResizeMove);
            document.addEventListener('mouseup', handleAiFeedbackResizeEnd);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        }

        return () => {
            document.removeEventListener('mousemove', handleAiFeedbackResizeMove);
            document.removeEventListener('mouseup', handleAiFeedbackResizeEnd);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isResizingAiFeedback]);

    // Function to update node data
    const updateNodeData = useCallback((nodeId: string, updates: Partial<Node['data']>) => {
        setNodes((nds) =>
            nds.map((node) =>
                node.id === nodeId
                    ? { ...node, data: { ...node.data, ...updates } }
                    : node
            )
        );

        // Update also selectedNode if it's the one being modified
        if (selectedNode?.id === nodeId) {
            setSelectedNode((prev) => prev ? { ...prev, data: { ...prev.data, ...updates } } : null);
        }
    }, [selectedNode]);

    const onNodesChange: OnNodesChange = useCallback((changes: NodeChange[]) => {
        setNodes((nds) => applyNodeChanges(changes, nds));

        // Check if selected node was deleted
        const deletedNodeId = changes.find((change) => change.type === "remove" && "id" in change)?.id;
        if (deletedNodeId === selectedNode?.id) {
            setSelectedNode(null);
        }

        // Update selected node if it was changed
        const selectedChange = changes.find((change) => {
            if (change.type === "select" && "id" in change) {
                return change.id === selectedNode?.id;
            }
            return false;
        });
        if (selectedChange && "selected" in selectedChange) {
            if (!selectedChange.selected) {
                setSelectedNode(null);
            }
        }
    }, [selectedNode]);

    const onEdgesChange: OnEdgesChange = useCallback((changes: EdgeChange[]) => {
        setEdges((eds) => applyEdgeChanges(changes, eds));

        // Check if selected edge was deleted
        const deletedEdgeId = changes.find((change) => change.type === "remove" && "id" in change)?.id;
        if (deletedEdgeId === selectedEdge?.id) {
            setSelectedEdge(null);
        }

        // Update selected edge if it was changed
        const selectedChange = changes.find((change) => {
            if (change.type === "select" && "id" in change) {
                return change.id === selectedEdge?.id;
            }
            return false;
        });
        if (selectedChange && "selected" in selectedChange) {
            if (!selectedChange.selected) {
                setSelectedEdge(null);
            }
        }
    }, [selectedEdge]);

    const onConnect: OnConnect = useCallback((params: Connection) => {
        setEdges((eds) => addEdge({
            ...params,
            type: 'smoothstep',
            animated: true,
        }, eds));
    }, []);

    const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
        setSelectedNode(node);
        setSelectedEdge(null);
    }, []);

    const onPaneClick = useCallback(() => {
        setSelectedNode(null);
        setSelectedEdge(null);
    }, []);

    const onEdgeClick: EdgeMouseHandler = useCallback((_event, edge) => {
        setSelectedEdge(edge);
        setSelectedNode(null);
    }, []);

    // Sync selectedEdge with edges state when edges change
    useEffect(() => {
        if (selectedEdge) {
            const updatedEdge = edges.find((e) => e.id === selectedEdge.id);
            if (updatedEdge && updatedEdge.label !== selectedEdge.label) {
                setSelectedEdge(updatedEdge);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [edges]);

    // Function to update edge data
    const updateEdgeData = useCallback((edgeId: string, updates: Partial<Edge>) => {
        setEdges((eds) =>
            eds.map((edge) => (edge.id === edgeId ? { ...edge, ...updates } : edge))
        );

        // Update also selectedEdge if it's the one being modified
        if (selectedEdge?.id === edgeId) {
            setSelectedEdge((prev) => (prev ? { ...prev, ...updates } : null));
        }
    }, [selectedEdge]);

    const handleAddNode = useCallback((nodeType: NodeType) => {
        const newNodeId = `node-${Date.now()}`;
        const defaultLabels: Record<NodeType, string> = {
            start: t('workflows.editor.defaultLabels.start'),
            agent: t('workflows.editor.defaultLabels.agent'),
            decision: t('workflows.editor.defaultLabels.decision'),
            action: t('workflows.editor.defaultLabels.action'),
            email: t('workflows.editor.defaultLabels.email'),
            end: t('workflows.editor.defaultLabels.end'),
        };

        // Calcola la posizione in alto a sinistra rispetto al viewport visibile del canvas
        if (canvasRef.current && flowHelpersRef.current) {
            const rect = canvasRef.current.getBoundingClientRect();
            // Conta quanti nodi esistono già per distribuirli meglio
            const existingNodesCount = nodes.length;
            // Posizione in alto a sinistra: offset dalla toolbar floating (circa 200px da sinistra, 100px dall'alto)
            // Aggiungi un offset verticale per ogni nuovo nodo per evitare sovrapposizioni
            const topLeftX = rect.left + 200;
            const topLeftY = rect.top + 100 + (existingNodesCount * 120); // 120px di offset verticale per nodo

            // Converti le coordinate dello schermo in coordinate del flow
            const flowPosition = flowHelpersRef.current.screenToFlowPosition({
                x: topLeftX,
                y: topLeftY,
            });

            const newNode: Node = {
                id: newNodeId,
                type: nodeType,
                position: flowPosition,
                data: {
                    label: defaultLabels[nodeType],
                },
            };
            setNodes((nds) => [...nds, newNode]);
        } else {
            // Fallback: usa una posizione in alto a sinistra approssimativa
            const existingNodesCount = nodes.length;
            const newNode: Node = {
                id: newNodeId,
                type: nodeType,
                position: { x: 200, y: 100 + (existingNodesCount * 120) },
                data: {
                    label: defaultLabels[nodeType],
                },
            };
            setNodes((nds) => [...nds, newNode]);
        }
    }, [t, nodes]);

    const handleSave = useCallback(() => {
        setIsSaving(true);

        const workflowNodes: WorkflowNode[] = nodes.map((node) => ({
            id: node.id,
            type: (node.type || "start") as NodeType,
            position: node.position,
            width: node.width,
            height: node.height,
            data: {
                label: (node.data?.label as string) || "",
                ...(node.data || {}),
            },
        }));

        const workflowEdges: WorkflowEdge[] = edges.map((edge) => ({
            id: edge.id,
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle || undefined,
            targetHandle: edge.targetHandle || undefined,
            // Usa la proprietà label nativa di React Flow (converte in stringa se presente)
            label: edge.label ? String(edge.label) : undefined,
        }));

        // Call onSave with account_ids, workflow name, is_active, and is_default
        // Note: status is NOT sent - it's AI-managed only
        onSave(
            workflowNodes,
            workflowEdges,
            selectedAccountIds.length > 0 ? selectedAccountIds : undefined,
            workflowName,
            isActive,
            isDefault !== undefined ? isDefault : false
        );

        // Show loading for 1 second, then success modal
        setTimeout(() => {
            setIsSaving(false);
            setShowSuccessModal(true);

            // Auto-close success modal after 2 seconds (but don't navigate)
            setTimeout(() => {
                setShowSuccessModal(false);
            }, 2000);
        }, 1000);
    }, [nodes, edges, selectedAccountIds, onSave, workflowName, isActive, isDefault]);

    const handleDeleteNode = useCallback(() => {
        if (selectedNode) {
            setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
            setEdges((eds) => eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
            setSelectedNode(null);
        }
    }, [selectedNode]);


    return (
        <div className="flex flex-col h-full">
            {/* Toolbar - Two rows for better layout */}
            <div className="border-b border-secondary bg-primary">
                {/* First Row: Back, Title, and Action Buttons */}
                <div className="flex items-center justify-between px-4 pt-3 pb-2">
                    <div className="flex items-center gap-4 min-w-0">
                        {/* Back Button */}
                        {showBackButton && backUrl && (
                            <Link to={backUrl}>
                                <Button size="sm" color="secondary" iconLeading={ArrowLeft}>
                                    {t('workflows.detail.back')}
                                </Button>
                            </Link>
                        )}

                        {/* Editable Title */}
                        {propWorkflowName !== undefined && onTitleChange && (
                            <>
                                {isEditingTitle ? (
                                    <div className="flex items-center gap-2">
                                        <Input
                                            ref={titleInputRef}
                                            defaultValue={workflowName || ''}
                                            onBlur={handleTitleBlur}
                                            onKeyDown={handleTitleKeyDown}
                                            placeholder={t('workflows.new.title')}
                                            className="text-xl font-semibold"
                                            size="md"
                                        />
                                    </div>
                                ) : (
                                    <div
                                        className="flex items-center gap-2 cursor-pointer group"
                                        onClick={handleTitleClick}
                                    >
                                        <h2 className="text-xl font-semibold text-primary truncate">
                                            {workflowName || t('workflows.new.title')}
                                        </h2>
                                        <Edit01 className="size-4 text-tertiary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Action Buttons - Save and Evaluate */}
                    <div className="flex items-center gap-2 shrink-0">
                        {/* Evaluate Workflow Button */}
                        {onEvaluate && (
                            <Button
                                size="sm"
                                color="secondary"
                                iconLeading={Atom02}
                                onClick={async () => {
                                    setIsEvaluating(true);
                                    try {
                                        await onEvaluate();
                                    } finally {
                                        setIsEvaluating(false);
                                    }
                                }}
                                isDisabled={isEvaluating}
                            >
                                {isEvaluating ? t('workflows.editor.evaluating') : t('workflows.editor.evaluate')}
                            </Button>
                        )}
                        <Button size="sm" color="primary" onClick={handleSave}>
                            {t('workflows.editor.save')}
                        </Button>
                    </div>
                </div>

                {/* Second Row: Status, Toggles, and Controls */}
                <div className="flex flex-wrap items-center gap-3 px-4 pb-3">
                    {/* Status Badge (read-only, AI-managed) - toggles AI feedback panel when there's feedback */}
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-secondary whitespace-nowrap">{t('workflows.editor.status')}:</span>
                        <button
                            type="button"
                            onClick={() => initialAiFeedback && initialAiFeedback.length > 0 && setIsAiFeedbackPanelOpen(!isAiFeedbackPanelOpen)}
                            className={`flex items-center gap-1 ${initialAiFeedback && initialAiFeedback.length > 0 ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                            disabled={!initialAiFeedback || initialAiFeedback.length === 0}
                        >
                            <BadgeWithIcon
                                type="color"
                                size="sm"
                                color={getStatusBadgeConfig(workflowStatus).color}
                                iconLeading={getStatusBadgeConfig(workflowStatus).icon}
                            >
                                {t(`workflows.status.${workflowStatus}`)}
                            </BadgeWithIcon>
                            {initialAiFeedback && initialAiFeedback.length > 0 && (
                                <InfoCircle className={`size-4 ${isAiFeedbackPanelOpen ? 'text-brand' : 'text-tertiary'}`} />
                            )}
                        </button>
                    </div>

                    {/* Separator */}
                    <div className="h-6 w-px bg-secondary" />

                    {/* Active Toggle (user-controllable) */}
                    <Toggle
                        isSelected={isActive}
                        onChange={setIsActive}
                        label={t('workflows.editor.isActive')}
                        size="sm"
                        isDisabled={workflowStatus !== 'ready' && workflowStatus !== 'disabled'}
                    />

                    {/* Default Toggle */}
                    <Toggle
                        isSelected={isDefault}
                        onChange={setIsDefault}
                        label={t('workflows.editor.isDefault')}
                        size="sm"
                    />

                    {/* Separator */}
                    <div className="h-6 w-px bg-secondary" />

                    {/* Related Accounts */}
                    <Button
                        size="sm"
                        color="secondary"
                        iconLeading={Users01}
                        onClick={() => setIsAccountsModalOpen(true)}
                    >
                        {t('workflows.editor.relatedAccounts')} ({selectedAccountIds.length})
                    </Button>

                    {/* Delete Node/Edge buttons (contextual) */}
                    {selectedNode && (
                        <Button size="sm" color="secondary-destructive" onClick={handleDeleteNode}>
                            {t('workflows.editor.deleteNode')}
                        </Button>
                    )}
                    {selectedEdge && (
                        <Button size="sm" color="secondary-destructive" onClick={() => {
                            setEdges((eds) => eds.filter((e) => e.id !== selectedEdge.id));
                            setSelectedEdge(null);
                        }}>
                            {t('workflows.editor.deleteEdge')}
                        </Button>
                    )}
                </div>
            </div>

            {/* Editor Area */}
            <div className="flex flex-1 overflow-hidden relative">
                {/* React Flow Canvas */}
                <div className="flex-1 relative" ref={canvasRef}>
                    {/* Floating Toolbar for Node Types */}
                    <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 bg-primary rounded-lg shadow-lg ring-1 ring-secondary p-2">
                        <ButtonGroup
                            size="sm"
                            selectedKeys={buttonGroupSelection}
                            onSelectionChange={() => {
                                // Reset selection immediately after click to prevent button from staying active
                                setButtonGroupSelection(new Set());
                            }}
                        >
                            <ButtonGroupItem
                                iconLeading={Play}
                                onClick={() => handleAddNode('start')}
                            >
                                {t('workflows.editor.nodeTypes.start')}
                            </ButtonGroupItem>
                            <ButtonGroupItem
                                iconLeading={Atom02}
                                onClick={() => handleAddNode('agent')}
                            >
                                {t('workflows.editor.nodeTypes.agent')}
                            </ButtonGroupItem>
                            <ButtonGroupItem
                                iconLeading={HelpCircle}
                                onClick={() => handleAddNode('decision')}
                            >
                                {t('workflows.editor.nodeTypes.decision')}
                            </ButtonGroupItem>
                            <ButtonGroupItem
                                iconLeading={Settings01}
                                onClick={() => handleAddNode('action')}
                            >
                                {t('workflows.editor.nodeTypes.action')}
                            </ButtonGroupItem>
                            <ButtonGroupItem
                                iconLeading={Mail01}
                                onClick={() => handleAddNode('email')}
                            >
                                {t('workflows.editor.nodeTypes.email')}
                            </ButtonGroupItem>
                            <ButtonGroupItem
                                iconLeading={XCircle}
                                onClick={() => handleAddNode('end')}
                            >
                                {t('workflows.editor.nodeTypes.end')}
                            </ButtonGroupItem>
                        </ButtonGroup>
                    </div>
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onNodeClick={onNodeClick}
                        onEdgeClick={onEdgeClick}
                        onPaneClick={onPaneClick}
                        nodeTypes={nodeTypes}
                        defaultEdgeOptions={{
                            type: ConnectionLineType.SmoothStep,
                            animated: true,
                        }}
                        connectionLineType={ConnectionLineType.SmoothStep}
                        nodesDraggable={true}
                        nodesConnectable={true}
                        elementsSelectable={true}
                        panOnDrag={true}
                        zoomOnScroll={true}
                        zoomOnPinch={true}
                        fitView
                    >
                        <Background />
                        <Controls />
                        <FlowHelper onFlowReady={(helpers) => { flowHelpersRef.current = helpers; }} />
                    </ReactFlow>
                </div>

                {/* Sidebar Properties */}
                {isSidebarOpen && (
                    <div
                        ref={sidebarRef}
                        className="border-l border-secondary bg-primary flex flex-col relative z-50"
                        style={{ width: `${sidebarWidth}px` }}
                    >
                        {/* Resize handle */}
                        <div
                            onMouseDown={handleResizeStart}
                            className={`absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-brand transition-colors ${isResizing ? 'bg-brand' : ''}`}
                            style={{ zIndex: 1000 }}
                        />

                        {/* Close button */}
                        <button
                            onClick={() => setIsSidebarOpen(false)}
                            className="absolute top-2 right-2 p-1 rounded hover:bg-secondary transition-colors z-10"
                            aria-label={t('workflows.editor.closeSidebar')}
                        >
                            <X className="size-4 text-tertiary" />
                        </button>
                        {/* Node Properties - Only when node is selected */}
                        {selectedNode && (
                            <div className="flex-1 overflow-y-auto p-4 pt-10">
                                <h3 className="text-lg font-semibold text-primary mb-4">{t('workflows.editor.nodeProperties')}</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-sm font-medium text-primary mb-1 block">Node ID</label>
                                        <div className="text-xs text-tertiary font-mono bg-secondary px-2 py-1 rounded select-all">{selectedNode.id}</div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-primary mb-1 block">{t('workflows.editor.nodeType')}</label>
                                        <div className="text-sm text-tertiary">{selectedNode.type}</div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-primary mb-1 block">{t('workflows.editor.nodeLabel')}</label>
                                        <Input
                                            value={(selectedNode.data?.label as string) || ''}
                                            onChange={(value) => updateNodeData(selectedNode.id, { label: value })}
                                            placeholder={t('workflows.editor.nodeLabel')}
                                        />
                                    </div>
                                    {/* Start Node - Trigger Configuration */}
                                    {selectedNode.type === "start" && (
                                        <>
                                            <div>
                                                <Select
                                                    label={t('workflows.editor.triggerType')}
                                                    placeholder={t('workflows.editor.selectTrigger')}
                                                    selectedKey={(selectedNode.data?.trigger_type as string) || null}
                                                    onSelectionChange={(key) => {
                                                        const triggerType = triggerTypes.find(tt => tt.event_type === key);
                                                        updateNodeData(selectedNode.id, {
                                                            trigger_type: key as string,
                                                            trigger_conditions: triggerType?.requires_conditions ? {} : undefined
                                                        });
                                                    }}
                                                    isDisabled={isLoadingTriggers}
                                                    items={triggerTypes.map(tt => ({
                                                        id: tt.event_type,
                                                        label: tt.label,
                                                        supportingText: tt.requires_conditions ? t('workflows.editor.requiresConditions') : undefined
                                                    }))}
                                                >
                                                    {(item) => <Select.Item key={item.id} {...item} />}
                                                </Select>
                                                {(() => {
                                                    const triggerType = selectedNode.data?.trigger_type;
                                                    if (triggerType && typeof triggerType === 'string') {
                                                        const description = triggerTypes.find(tt => tt.event_type === triggerType)?.description || '';
                                                        return description ? (
                                                            <p className="text-xs text-tertiary mt-1">
                                                                {description}
                                                            </p>
                                                        ) : null;
                                                    }
                                                    return null;
                                                })()}
                                            </div>
                                            {/* Trigger Conditions - Dynamic based on selected trigger type */}
                                            {selectedNode.data?.trigger_type && (() => {
                                                const selectedTrigger = triggerTypes.find(tt => tt.event_type === selectedNode.data?.trigger_type);
                                                if (!selectedTrigger?.requires_conditions || !selectedTrigger.condition_fields?.length) return null;

                                                return (
                                                    <div className="border border-secondary rounded-lg p-3 space-y-3">
                                                        <label className="text-sm font-medium text-primary block">{t('workflows.editor.triggerConditions')}</label>
                                                        {selectedTrigger.condition_fields.map((field) => (
                                                            <div key={field.name}>
                                                                {field.type === 'enum' && field.options ? (
                                                                    <Select
                                                                        label={field.label}
                                                                        placeholder={t('workflows.editor.selectValue')}
                                                                        selectedKey={((selectedNode.data?.trigger_conditions as Record<string, unknown>)?.[field.name] as string) || null}
                                                                        onSelectionChange={(key) => {
                                                                            const currentConditions = (selectedNode.data?.trigger_conditions as Record<string, unknown>) || {};
                                                                            updateNodeData(selectedNode.id, {
                                                                                trigger_conditions: {
                                                                                    ...currentConditions,
                                                                                    [field.name]: key
                                                                                }
                                                                            });
                                                                        }}
                                                                        items={field.options.map(opt => ({
                                                                            id: opt.value,
                                                                            label: opt.label
                                                                        }))}
                                                                    >
                                                                        {(item) => <Select.Item key={item.id} {...item} />}
                                                                    </Select>
                                                                ) : (
                                                                    <Input
                                                                        label={field.label}
                                                                        value={((selectedNode.data?.trigger_conditions as Record<string, unknown>)?.[field.name] as string) || ''}
                                                                        onChange={(value) => {
                                                                            const currentConditions = (selectedNode.data?.trigger_conditions as Record<string, unknown>) || {};
                                                                            updateNodeData(selectedNode.id, {
                                                                                trigger_conditions: {
                                                                                    ...currentConditions,
                                                                                    [field.name]: value
                                                                                }
                                                                            });
                                                                        }}
                                                                        placeholder={field.description}
                                                                    />
                                                                )}
                                                                {field.description && field.type !== 'enum' && (
                                                                    <p className="text-xs text-tertiary mt-1">{field.description}</p>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            })()}
                                            {/* Instruction for Start Node */}
                                            <div>
                                                <TextArea
                                                    label={t('workflows.editor.startNodeInstruction')}
                                                    value={(selectedNode.data?.instruction as string) || ''}
                                                    onChange={(value) => updateNodeData(selectedNode.id, { instruction: value })}
                                                    placeholder={t('workflows.editor.startNodeInstructionPlaceholder')}
                                                    rows={3}
                                                />
                                            </div>
                                        </>
                                    )}
                                    {selectedNode.type === "agent" && (
                                        <div>
                                            <label className="text-sm font-medium text-primary mb-1 block">{t('workflows.editor.agentType')}</label>
                                            <AgentSelector
                                                selectedAgentId={selectedNode.data?.agent_id as string | undefined}
                                                agents={agents}
                                                onSelect={(agentId) => {
                                                    const selectedAgent = agents.find(a => a.id === agentId);
                                                    updateNodeData(selectedNode.id, {
                                                        agent_id: agentId,
                                                        label: selectedAgent?.name || selectedNode.data?.label || ''
                                                    });
                                                }}
                                                isLoading={isLoadingAgents}
                                            />
                                        </div>
                                    )}
                                    {selectedNode.type === "action" && (
                                        <>
                                            <div>
                                                <TextArea
                                                    label={t('workflows.editor.instructions')}
                                                    value={(selectedNode.data?.instructions as string) || (selectedNode.data?.instruction as string) || ''}
                                                    onChange={(value) => updateNodeData(selectedNode.id, { instructions: value, instruction: value })}
                                                    placeholder={t('workflows.editor.instructionsPlaceholder')}
                                                    rows={4}
                                                />
                                            </div>
                                            {/* Actions List */}
                                            <div className="border border-secondary rounded-lg p-3 space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <label className="text-sm font-medium text-primary">{t('workflows.editor.availableActions')}</label>
                                                    <Select
                                                        placeholder={t('workflows.editor.addAction')}
                                                        selectedKey={null}
                                                        onSelectionChange={(key) => {
                                                            if (!key) return;
                                                            const currentActions = (selectedNode.data?.actions as ActionReference[]) || [];
                                                            const newAction: ActionReference = {
                                                                action_id: key as string,
                                                                params: {},
                                                                order: currentActions.length + 1
                                                            };
                                                            updateNodeData(selectedNode.id, {
                                                                actions: [...currentActions, newAction]
                                                            });
                                                        }}
                                                        isDisabled={isLoadingActions}
                                                        items={workflowActions.map(action => ({
                                                            id: action.action_id,
                                                            label: action.name,
                                                            supportingText: action.is_tool ? t('workflows.editor.toolAction') : action.category_label
                                                        }))}
                                                        size="sm"
                                                    >
                                                        {(item) => <Select.Item key={item.id} {...item} />}
                                                    </Select>
                                                </div>
                                                {/* Configured Actions List */}
                                                {((selectedNode.data?.actions as ActionReference[]) || []).length === 0 ? (
                                                    <p className="text-xs text-tertiary italic">{t('workflows.editor.noActionsConfigured')}</p>
                                                ) : (
                                                    <div className="space-y-2">
                                                        {((selectedNode.data?.actions as ActionReference[]) || []).map((action, index) => {
                                                            const actionDef = workflowActions.find(a => a.action_id === action.action_id);
                                                            return (
                                                                <div key={`${action.action_id}-${index}`} className="bg-secondary rounded-md p-2 space-y-2">
                                                                    <div className="flex items-center justify-between">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-xs font-mono text-tertiary bg-primary px-1.5 py-0.5 rounded">{index + 1}</span>
                                                                            <span className="text-sm font-medium text-primary">{actionDef?.name || action.action_id}</span>
                                                                            {actionDef?.is_dangerous && (
                                                                                <span className="text-xs bg-utility-error-50 text-utility-error-600 px-1.5 py-0.5 rounded">{t('workflows.editor.dangerousAction')}</span>
                                                                            )}
                                                                            {actionDef?.is_tool && (
                                                                                <span className="text-xs bg-utility-brand-50 text-utility-brand-600 px-1.5 py-0.5 rounded">{t('workflows.editor.toolAction')}</span>
                                                                            )}
                                                                        </div>
                                                                        <div className="flex items-center gap-1">
                                                                            {index > 0 && (
                                                                                <button
                                                                                    onClick={() => {
                                                                                        const actions = [...((selectedNode.data?.actions as ActionReference[]) || [])];
                                                                                        [actions[index - 1], actions[index]] = [actions[index], actions[index - 1]];
                                                                                        actions.forEach((a, i) => a.order = i + 1);
                                                                                        updateNodeData(selectedNode.id, { actions });
                                                                                    }}
                                                                                    className="p-1 text-tertiary hover:text-primary hover:bg-primary rounded"
                                                                                    title={t('workflows.editor.moveUp')}
                                                                                >
                                                                                    ↑
                                                                                </button>
                                                                            )}
                                                                            {index < ((selectedNode.data?.actions as ActionReference[]) || []).length - 1 && (
                                                                                <button
                                                                                    onClick={() => {
                                                                                        const actions = [...((selectedNode.data?.actions as ActionReference[]) || [])];
                                                                                        [actions[index], actions[index + 1]] = [actions[index + 1], actions[index]];
                                                                                        actions.forEach((a, i) => a.order = i + 1);
                                                                                        updateNodeData(selectedNode.id, { actions });
                                                                                    }}
                                                                                    className="p-1 text-tertiary hover:text-primary hover:bg-primary rounded"
                                                                                    title={t('workflows.editor.moveDown')}
                                                                                >
                                                                                    ↓
                                                                                </button>
                                                                            )}
                                                                            <button
                                                                                onClick={() => {
                                                                                    const actions = ((selectedNode.data?.actions as ActionReference[]) || []).filter((_, i) => i !== index);
                                                                                    actions.forEach((a, i) => a.order = i + 1);
                                                                                    updateNodeData(selectedNode.id, { actions });
                                                                                }}
                                                                                className="p-1 text-utility-error-500 hover:text-utility-error-600 hover:bg-utility-error-50 rounded"
                                                                                title={t('workflows.editor.removeAction')}
                                                                            >
                                                                                ✕
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    {actionDef?.description && (
                                                                        <p className="text-xs text-tertiary">{actionDef.description}</p>
                                                                    )}
                                                                    {/* Action Parameters */}
                                                                    {actionDef?.params_schema && actionDef.params_schema.length > 0 && (
                                                                        <div className="pt-2 border-t border-primary space-y-2">
                                                                            {actionDef.params_schema.map((param) => (
                                                                                <div key={param.name}>
                                                                                    {param.type === 'enum' ? (
                                                                                        <Select
                                                                                            label={param.label}
                                                                                            placeholder={param.placeholder || t('workflows.editor.selectValue')}
                                                                                            selectedKey={(action.params[param.name] as string) || null}
                                                                                            onSelectionChange={(key) => {
                                                                                                const actions = [...((selectedNode.data?.actions as ActionReference[]) || [])];
                                                                                                actions[index] = {
                                                                                                    ...actions[index],
                                                                                                    params: { ...actions[index].params, [param.name]: key }
                                                                                                };
                                                                                                updateNodeData(selectedNode.id, { actions });
                                                                                            }}
                                                                                            size="sm"
                                                                                            items={[]}
                                                                                        >
                                                                                            {() => null}
                                                                                        </Select>
                                                                                    ) : param.type === 'template' || param.type === 'string' ? (
                                                                                        <Input
                                                                                            label={param.label}
                                                                                            value={(action.params[param.name] as string) || param.default || ''}
                                                                                            onChange={(value) => {
                                                                                                const actions = [...((selectedNode.data?.actions as ActionReference[]) || [])];
                                                                                                actions[index] = {
                                                                                                    ...actions[index],
                                                                                                    params: { ...actions[index].params, [param.name]: value }
                                                                                                };
                                                                                                updateNodeData(selectedNode.id, { actions });
                                                                                            }}
                                                                                            placeholder={param.placeholder || param.description}
                                                                                        />
                                                                                    ) : null}
                                                                                    {param.description && param.type !== 'template' && (
                                                                                        <p className="text-xs text-tertiary mt-0.5">{param.description}</p>
                                                                                    )}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                    {selectedNode.type === "email" && (
                                        <>
                                            <div>
                                                <label className="text-sm font-medium text-primary mb-1 block">{t('workflows.editor.emailNodeTemplateFilter')}</label>
                                                <Select
                                                    placeholder={t('workflows.editor.selectValue')}
                                                    selectedKey={(selectedNode.data?.template_filter as string) || "system_only"}
                                                    onSelectionChange={(key) => {
                                                        const filter = key as "system_only" | "company";
                                                        updateNodeData(selectedNode.id, {
                                                            template_filter: filter,
                                                            template_company_ids: filter === "company" ? (selectedNode.data?.template_company_ids as string[] | undefined) || [] : undefined,
                                                        });
                                                    }}
                                                    items={[
                                                        { id: "system_only", label: t('workflows.editor.emailNodeFilterSystem') },
                                                        { id: "company", label: t('workflows.editor.emailNodeFilterCompany') },
                                                    ]}
                                                >
                                                    {(item) => <Select.Item key={item.id} {...item} />}
                                                </Select>
                                            </div>
                                            {selectedNode.data?.template_filter === "company" && (
                                                <div>
                                                    <label className="text-sm font-medium text-primary mb-1 block">{t('workflows.editor.emailNodeCompany')}</label>
                                                    <Select
                                                        placeholder={t('workflows.editor.emailNodeSelectCompany')}
                                                        selectedKey={(selectedNode.data?.template_company_ids as string[])?.[0] ?? null}
                                                        onSelectionChange={(key) => {
                                                            const companyId = key as string;
                                                            updateNodeData(selectedNode.id, {
                                                                template_company_ids: companyId ? [companyId] : [],
                                                            });
                                                        }}
                                                        items={shipperAccounts.map((acc) => ({ id: acc.id, label: acc.legalName || acc.email }))}
                                                    >
                                                        {(item) => <Select.Item key={item.id} {...item} />}
                                                    </Select>
                                                </div>
                                            )}
                                            <div>
                                                <Select
                                                    label={t('workflows.editor.emailNodeSelectTemplate')}
                                                    placeholder={t('workflows.editor.emailNodeSelectTemplate')}
                                                    selectedKey={(selectedNode.data?.template_id as string) || null}
                                                    onSelectionChange={(key) => {
                                                        const templateId = key as string;
                                                        const template = outboundTemplates.find((t) => t.template_id === templateId);
                                                        updateNodeData(selectedNode.id, {
                                                            template_id: templateId,
                                                            label: template?.name ?? selectedNode.data?.label,
                                                        });
                                                    }}
                                                    isDisabled={isLoadingTemplates}
                                                    items={outboundTemplates.map((tmpl) => ({
                                                        id: tmpl.template_id,
                                                        label: tmpl.name,
                                                        supportingText: tmpl.company_id ? t('workflows.editor.emailNodeFilterCompany') : t('workflows.editor.emailNodeFilterSystem'),
                                                    }))}
                                                >
                                                    {(item) => <Select.Item key={item.id} {...item} />}
                                                </Select>
                                            </div>
                                            <div>
                                                <TextArea
                                                    label={t('workflows.editor.emailNodeRules')}
                                                    value={(selectedNode.data?.rules as string) || ''}
                                                    onChange={(value) => updateNodeData(selectedNode.id, { rules: value })}
                                                    placeholder={t('workflows.editor.emailNodeRulesPlaceholder')}
                                                    rows={4}
                                                />
                                            </div>
                                            <div>
                                                <Input
                                                    label={t('workflows.editor.emailNodeTo')}
                                                    value={(selectedNode.data?.to as string) || ''}
                                                    onChange={(value) => updateNodeData(selectedNode.id, { to: value })}
                                                    placeholder={t('workflows.editor.emailNodeToPlaceholder')}
                                                />
                                            </div>
                                        </>
                                    )}
                                    {selectedNode.type === "decision" && (
                                        <div>
                                            <TextArea
                                                label={t('workflows.editor.instructions')}
                                                value={(selectedNode.data?.instructions as string) || ''}
                                                onChange={(value) => updateNodeData(selectedNode.id, { instructions: value })}
                                                placeholder={t('workflows.editor.instructionsPlaceholder')}
                                                rows={4}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {/* Edge Properties - Only when edge is selected */}
                        {selectedEdge && (
                            <div className="flex-1 overflow-y-auto p-4 pt-10">
                                <h3 className="text-lg font-semibold text-primary mb-4">{t('workflows.editor.edgeProperties')}</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-sm font-medium text-primary mb-1 block">{t('workflows.editor.edgeLabel')}</label>
                                        <Input
                                            value={(selectedEdge.label || '') as string}
                                            onChange={(value) => updateEdgeData(selectedEdge.id, { label: value })}
                                            placeholder={t('workflows.editor.edgeLabelPlaceholder')}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {/* Sidebar toggle button when closed */}
                {!isSidebarOpen && (selectedNode || selectedEdge) && (
                    <button
                        onClick={() => setIsSidebarOpen(true)}
                        className="absolute top-2 right-2 p-2 rounded bg-primary border border-secondary shadow-sm hover:bg-secondary transition-colors z-10"
                        aria-label={t('workflows.editor.openSidebar')}
                    >
                        <ChevronLeft className="size-4 text-primary" />
                    </button>
                )}

                {/* AI Feedback Panel - Separate collapsible panel on the right */}
                {isAiFeedbackPanelOpen && initialAiFeedback && initialAiFeedback.length > 0 && (
                    <div
                        ref={aiFeedbackPanelRef}
                        className="border-l border-secondary bg-primary flex flex-col relative z-40"
                        style={{ width: `${aiFeedbackPanelWidth}px` }}
                    >
                        {/* Resize handle */}
                        <div
                            onMouseDown={handleAiFeedbackResizeStart}
                            className={`absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-brand transition-colors ${isResizingAiFeedback ? 'bg-brand' : ''}`}
                            style={{ zIndex: 1000 }}
                        />

                        {/* Panel Header */}
                        <div className="flex items-center justify-between p-4 border-b border-secondary">
                            <div className="flex items-center gap-2">
                                <div className={`p-1.5 rounded-lg ${
                                    workflowStatus === 'ready' ? 'bg-utility-success-50' :
                                    workflowStatus === 'invalid' ? 'bg-utility-error-50' :
                                    workflowStatus === 'needs_clarification' ? 'bg-utility-warning-50' :
                                    'bg-secondary'
                                }`}>
                                    {workflowStatus === 'ready' ? <CheckCircle className="size-4 text-utility-success-600" /> :
                                     workflowStatus === 'invalid' ? <XCircle className="size-4 text-utility-error-600" /> :
                                     workflowStatus === 'needs_clarification' ? <AlertCircle className="size-4 text-utility-warning-600" /> :
                                     <InfoCircle className="size-4 text-tertiary" />}
                                </div>
                                <div>
                                    <h3 className="text-sm font-semibold text-primary">{t('workflows.editor.aiFeedback.title')}</h3>
                                    {initialAnalyzedAt && (
                                        <p className="text-xs text-tertiary">
                                            {new Date(initialAnalyzedAt).toLocaleDateString()}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={() => setIsAiFeedbackPanelOpen(false)}
                                className="p-1 rounded hover:bg-secondary transition-colors"
                                aria-label={t('workflows.editor.closeSidebar')}
                            >
                                <X className="size-4 text-tertiary" />
                            </button>
                        </div>

                        {/* Feedback Items */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {initialAiFeedback.map((feedback, index) => (
                                <div
                                    key={index}
                                    className={`p-3 rounded-lg border ${
                                        feedback.type === 'error' ? 'bg-utility-error-50 border-utility-error-200' :
                                        feedback.type === 'warning' ? 'bg-utility-warning-50 border-utility-warning-200' :
                                        feedback.type === 'info' ? 'bg-utility-brand-50 border-utility-brand-200' :
                                        'bg-secondary border-secondary'
                                    }`}
                                >
                                    <div className="flex items-start gap-2">
                                        <div className="shrink-0 mt-0.5">
                                            {feedback.type === 'error' ? <XCircle className="size-4 text-utility-error-600" /> :
                                             feedback.type === 'warning' ? <AlertCircle className="size-4 text-utility-warning-600" /> :
                                             feedback.type === 'info' ? <InfoCircle className="size-4 text-utility-brand-600" /> :
                                             <HelpCircle className="size-4 text-tertiary" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            {feedback.node_id && (
                                                <p className="text-xs font-medium text-tertiary mb-1 font-mono">
                                                    {t('workflows.editor.aiFeedback.nodeId')}: {feedback.node_id}
                                                </p>
                                            )}
                                            <p className="text-sm text-primary">{feedback.message}</p>
                                            {feedback.suggestions && feedback.suggestions.length > 0 && (
                                                <div className="mt-2">
                                                    <p className="text-xs font-medium text-secondary flex items-center gap-1 mb-1">
                                                        <Lightbulb02 className="size-3" />
                                                        {t('workflows.editor.aiFeedback.suggestions')}:
                                                    </p>
                                                    <ul className="list-disc list-inside space-y-0.5">
                                                        {feedback.suggestions.map((suggestion, idx) => (
                                                            <li key={idx} className="text-xs text-tertiary">{suggestion}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* AI Feedback Panel toggle button when closed */}
                {!isAiFeedbackPanelOpen && initialAiFeedback && initialAiFeedback.length > 0 && (
                    <button
                        onClick={() => setIsAiFeedbackPanelOpen(true)}
                        className={`absolute top-2 p-2 rounded bg-primary border shadow-sm hover:bg-secondary transition-colors z-10 ${
                            isSidebarOpen ? 'right-[calc(var(--sidebar-width)+8px)]' : 'right-10'
                        } ${
                            workflowStatus === 'invalid' ? 'border-utility-error-300' :
                            workflowStatus === 'needs_clarification' ? 'border-utility-warning-300' :
                            workflowStatus === 'ready' ? 'border-utility-success-300' :
                            'border-secondary'
                        }`}
                        style={{ '--sidebar-width': isSidebarOpen ? `${sidebarWidth}px` : '0px' } as React.CSSProperties}
                        aria-label={t('workflows.editor.aiFeedback.title')}
                    >
                        <Lightbulb02 className={`size-4 ${
                            workflowStatus === 'invalid' ? 'text-utility-error-600' :
                            workflowStatus === 'needs_clarification' ? 'text-utility-warning-600' :
                            workflowStatus === 'ready' ? 'text-utility-success-600' :
                            'text-tertiary'
                        }`} />
                    </button>
                )}
            </div>

            {/* Loading Modal */}
            <DialogTrigger isOpen={isSaving}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center gap-3">
                                    <div className="size-5 animate-spin rounded-full border-2 border-brand border-t-transparent" />
                                    <p className="text-primary font-medium">{t('workflows.editor.savingWorkflow')}</p>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Success Modal */}
            <DialogTrigger isOpen={showSuccessModal}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center gap-3">
                                    <CheckCircle className="size-5 text-utility-success-600" />
                                    <p className="text-primary font-medium">{t('workflows.editor.workflowSaved')}</p>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Related Accounts Modal */}
            <DialogTrigger isOpen={isAccountsModalOpen} onOpenChange={setIsAccountsModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-4xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">{t('workflows.editor.relatedAccounts')}</h2>
                                    <Button
                                        size="sm"
                                        color="secondary"
                                        iconLeading={X}
                                        onClick={handleAccountsModalClose}
                                    >
                                        {t('workflows.editor.close')}
                                    </Button>
                                </div>

                                {/* Search Input */}
                                <div className="mb-4">
                                    <Input
                                        value={accountSearchTerm}
                                        onChange={setAccountSearchTerm}
                                        placeholder={t('workflows.editor.searchAccounts')}
                                    />
                                </div>

                                {/* Accounts List */}
                                <div className="max-h-[500px] overflow-y-auto space-y-2">
                                    {filteredAccounts.length === 0 ? (
                                        <div className="text-center py-8 text-tertiary">
                                            {t('workflows.editor.noAccountsFound')}
                                        </div>
                                    ) : (
                                        filteredAccounts.map((account) => {
                                            const isSelected = selectedAccountIds.includes(account.id);
                                            return (
                                                <div
                                                    key={account.id}
                                                    onClick={() => handleAccountToggle(account.id)}
                                                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${isSelected
                                                        ? 'bg-primary border-brand ring-1 ring-brand'
                                                        : 'bg-primary border-secondary hover:border-primary'
                                                        }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleAccountToggle(account.id)}
                                                        className="size-4 rounded border-secondary text-brand focus:ring-brand"
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-medium text-primary truncate">
                                                            {account.legalName}
                                                        </div>
                                                        <div className="text-xs text-tertiary truncate">
                                                            {account.email}
                                                        </div>
                                                        {account.taxId && (
                                                            <div className="text-xs text-tertiary">
                                                                {t('workflows.editor.taxId')}: {account.taxId}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>

                                {/* Footer */}
                                <div className="flex items-center justify-between mt-6 pt-4 border-t border-secondary">
                                    <div className="text-sm text-tertiary">
                                        {t('workflows.editor.selectedCount', { count: selectedAccountIds.length })}
                                    </div>
                                    <Button
                                        size="md"
                                        color="primary"
                                        onClick={handleAccountsModalClose}
                                    >
                                        {t('workflows.editor.save')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};

// Wrapper component that provides ReactFlow context
export const WorkflowEditor = (props: WorkflowEditorProps) => {
    return (
        <ReactFlowProvider>
            <WorkflowEditorInner {...props} />
        </ReactFlowProvider>
    );
};
